import { useCallback, useContext, useEffect, useState } from 'react';
import { SecondaryMenuItemTitle } from '../../../component';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import { useTranslation } from 'react-i18next';
import type { GridCellParams } from '@mui/x-data-grid-pro';
import type { SidePanelContextColumnsPropsType } from '../../SidePanelModelsProvider';
import { SidePanelProviderContext } from '../../SidePanelModelsProvider';
import { useTableManipulation } from '../../../../GridDndEditor/Block/Table/useTableManipulation';
import { useTableContentUndoRedo } from '../../../../hooks/useTableContentUndoRedo';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { getIsCellEditingAllowed } from 'components/editor/GridDndEditor/Block/Table/tableUtils/customCellHelpers';
import { type RootState, useAppSelector } from '../../../../grid/reduxStore/Store.ts';
import { selectContentTable } from '../../../../grid/reduxStore/editorSlice.ts';

const MultiplierEditingToggle = () => {
  const { t } = useTranslation();
  const { toggledTableSettingsPanel } = useContext(SidePanelProviderContext) as SidePanelContextColumnsPropsType;
  const { selectedBlockIdByWrapper, selectedSectionId } = useContext(SelectionContext);
  const sectionId = selectedSectionId as string;
  const blockId = selectedBlockIdByWrapper as string;
  const { addCustomFieldToRow } = useTableManipulation();
  const label = { inputProps: { 'aria-label': 'Allow recipient to edit' } };
  const selectedModel = toggledTableSettingsPanel?.tableApi.selectedModel || undefined;
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const { row: selectedRow, field, colDef } = selectedModel as GridCellParams;
  const tableData = useAppSelector((state: RootState) => selectContentTable(state, sectionId, blockId)) || [];
  const [isToggleAvailable, setIsToggleAvailable] = useState(true);
  const [isEditingActive, setIsEditingActive] = useState<boolean>(getIsCellEditingAllowed({ row: selectedRow, colDef }));

  const handleAllowEditingChange = useCallback(async () => {
    if (!selectedModel) return;
    const { id } = selectedRow;
    const currentIsEditingActive = !isEditingActive;
    setIsToggleAvailable(false);
    setIsEditingActive(currentIsEditingActive);

    try {
      const cellConfigObject = { isEditingActive: currentIsEditingActive };
      const updatedTableData = addCustomFieldToRow({ blockId, sectionId, field, id, cellConfigObject });
      if (!updatedTableData) return;

      handleTableContentChanged(blockId, sectionId, tableData, updatedTableData);
      setIsToggleAvailable(true);
    } catch (error) {
      setIsToggleAvailable(true);
      throw new Error('Error while updating table data');
    }
  }, [blockId, sectionId, addCustomFieldToRow, selectedModel]);

  useEffect(() => {
    if (!tableData || !selectedRow?.id || !field) return;

    const currentRow = tableData.rows?.find((row) => row.id === selectedRow.id);
    const currentColumn = tableData.columns?.find((col) => col.field === field);
    if (!currentRow || !currentColumn) return;

    setIsEditingActive(getIsCellEditingAllowed({ row: currentRow, colDef: currentColumn }));
  }, [tableData, selectedModel]);

  return (
    <>
      <SecondaryMenuItemTitle>{t('document.grid.table.setting.allow_edit_multiplier')}</SecondaryMenuItemTitle>
      <Grid>
        <Switch
          disabled={!isToggleAvailable}
          data-testid="pricing-allow-editing-toggle"
          {...label}
          checked={isEditingActive}
          onChange={handleAllowEditingChange}
        />
      </Grid>
    </>
  );
};

export default MultiplierEditingToggle;
